"use strict";
// Copyright (c) 2020, Brandon Lehmann
//
// Redistribution and use in source and binary forms, with or without modification, are
// permitted provided that the following conditions are met:
//
// 1. Redistributions of source code must retain the above copyright notice, this list of
//    conditions and the following disclaimer.
//
// 2. Redistributions in binary form must reproduce the above copyright notice, this list
//    of conditions and the following disclaimer in the documentation and/or other
//    materials provided with the distribution.
//
// 3. Neither the name of the copyright holder nor the names of its contributors may be
//    used to endorse or promote products derived from this software without specific
//    prior written permission.
//
// THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY
// EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF
// MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL
// THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
// SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
// PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
// INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
// STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF
// THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __exportStar = (this && this.__exportStar) || function(m, exports) {
    for (var p in m) if (p !== "default" && !Object.prototype.hasOwnProperty.call(exports, p)) __createBinding(exports, m, p);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Crypto = void 0;
const types_1 = require("../../types");
const version_1 = require("./version");
__exportStar(require("../../types"), exports);
class Crypto extends types_1.CryptoModule {
    /**
     * We cannot create a new instance using this method as we need to await the
     * loading of an underlying module, hence, we need to await the static
     * init() method on this class to receive an instance of the class
     *
     * @protected
     */
    // eslint-disable-next-line no-useless-constructor
    constructor() {
        super();
    }
    /**
     * Initializes a new instance of this class after attempting to load
     * the underlying cryptographic library in the order the fastest
     * running library to the slowest
     */
    static async init(externalLibrary = {}) {
        this.external_library = externalLibrary;
        if (!this.runtime_configuration.library) {
            if (await this.load_wasm_library()) {
                return new Crypto();
            }
            if (await this.load_js_library()) {
                return new Crypto();
            }
            throw new Error('Could not initialize an underlying library');
        }
        return new Crypto();
    }
    /**
     * Forces the library to use the Javascript ASM.js underlying
     * cryptographic library if it can
     */
    static async force_js_library() {
        return this.load_js_library();
    }
    /**
     * Forces the library to use the WASM underlying cryptographic
     * library if it can
     */
    static async force_wasm_library() {
        return this.load_wasm_library();
    }
    /**
     * Attempts to load a script from a URL
     *
     * @param url
     * @private
     */
    static async load(url) {
        if (!await Crypto.url_exists(url)) {
            return false;
        }
        return new Promise((resolve, reject) => {
            const elem = document.createElement('script');
            elem.src = url;
            elem.defer = true;
            elem.async = false;
            elem.crossOrigin = 'anonymous';
            elem.referrerPolicy = 'no-referrer';
            elem.addEventListener('load', () => resolve(true));
            elem.addEventListener('error', () => function (event) {
                return reject(new Error(event.message));
            });
            document.head.appendChild(elem);
        });
    }
    /**
     * Attempts to initialize the underlying cryptographic library from an url
     *
     * @param url
     * @param type
     * @private
     */
    static async load_library(url, type) {
        if (!await this.load(url)) {
            return false;
        }
        if (!window.CryptoModule) {
            return false;
        }
        const module = await (new window.CryptoModule());
        if (Object.getOwnPropertyNames(module).length === 0 || typeof module.sha3 === 'undefined') {
            return false;
        }
        types_1.CryptoModule.runtime_configuration = {
            type,
            library: module
        };
        return true;
    }
    /**
     * Attempts to load the Javascript ASM.js module as the underlying
     * cryptographic library
     *
     * @private
     */
    static async load_js_library() {
        if (await this.load_library(`https://cdn.jsdelivr.net/@gibme/crypto@${version_1.version}/dist/crypto-module.js`, types_1.LibraryType.JS)) {
            return true;
        }
        // this is a fallback
        return this.load_library('https://testmodule.pages.dev/crypto-module.js', types_1.LibraryType.JS);
    }
    /**
     * Attempts to load the WASM module as the underlying cryptographic
     * library
     *
     * @private
     */
    static async load_wasm_library() {
        if (await this.load_library(`https://cdn.jsdelivr.net/@gibme/crypto@${version_1.version}/dist/crypto-module-wasm.js`, types_1.LibraryType.WASM)) {
            return true;
        }
        // this is a fallback
        return this.load_library('https://testmodule.pages.dev/crypto-module-wasm.js', types_1.LibraryType.WASM);
    }
    /**
     * Checks if the URL exists
     *
     * @param url
     * @private
     */
    static async url_exists(url) {
        const response = await fetch(url, { method: 'HEAD' });
        return response.ok;
    }
}
exports.default = Crypto;
exports.Crypto = Crypto;
//# sourceMappingURL=index.js.map